import React from 'react';
import { TrendingUp, DollarSign, Percent } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров дивидендов
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  setErrors,
  isCalculating,
  currencyTypes
}) => {
  // Расчет текущей суммы инвестиций
  const initialInvestment = (parseFloat(formData.stockPrice) || 0) * (parseFloat(formData.sharesCount) || 0);
  
  // Расчет дивидендной доходности
  const dividendYield = ((parseFloat(formData.dividendPerShare) || 0) * getPaymentsPerYear(formData.dividendFrequency)) / (parseFloat(formData.stockPrice) || 1) * 100;
  
  // Получение символа валюты
  const currencyObj = currencyTypes.find(c => c.id === formData.currency);
  const currencySymbol = currencyObj ? currencyObj.symbol : '₽';
  
  // Обработчик изменения цены акции
  const handleStockPriceChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      stockPrice: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.stockPrice) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.stockPrice;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения количества акций
  const handleSharesCountChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      sharesCount: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.sharesCount) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.sharesCount;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения дивиденда на акцию
  const handleDividendPerShareChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      dividendPerShare: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.dividendPerShare) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.dividendPerShare;
        return newErrors;
      });
    }
  };
  
  // Предопределенные суммы инвестиций для быстрого выбора
  const predefinedInvestments = [
    { value: '10000', label: '10 тыс.' },
    { value: '50000', label: '50 тыс.' },
    { value: '100000', label: '100 тыс.' },
    { value: '1000000', label: '1 млн' }
  ];
  
  // Предопределенные периоды прогнозирования для быстрого выбора
  const predefinedPeriods = [
    { value: '1', label: '1 год' },
    { value: '5', label: '5 лет' },
    { value: '10', label: '10 лет' },
    { value: '20', label: '20 лет' }
  ];
  
  // Предопределенные дивидендные доходности для быстрого выбора
  const predefinedYields = [
    { value: '2', label: '2%' },
    { value: '5', label: '5%' },
    { value: '8', label: '8%' },
    { value: '10', label: '10%' }
  ];
  
  // Обработчик выбора предустановленной суммы инвестиций
  const handleSetInvestment = (amount) => {
    // Приводим акции к целому числу, сохраняя примерно ту же сумму инвестиций
    const stockPrice = parseFloat(formData.stockPrice) || 1000;
    const sharesCount = Math.round(amount / stockPrice);
    
    setFormData(prev => ({
      ...prev,
      sharesCount: sharesCount.toString()
    }));
  };
  
  // Обработчик выбора предустановленной дивидендной доходности
  const handleSetYield = (yieldPercent) => {
    // Рассчитываем дивиденд на акцию, исходя из выбранной доходности
    const stockPrice = parseFloat(formData.stockPrice) || 1000;
    const paymentsPerYear = getPaymentsPerYear(formData.dividendFrequency);
    const dividendPerShare = (stockPrice * yieldPercent / 100) / paymentsPerYear;
    
    setFormData(prev => ({
      ...prev,
      dividendPerShare: dividendPerShare.toFixed(2)
    }));
  };
  
  // Обработчик выбора предустановленного периода прогнозирования
  const handleSetPeriod = (period) => {
    setFormData(prev => ({
      ...prev,
      projectionPeriod: period
    }));
  };
  
  // Функция расчета
  const calculateDividends = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Вспомогательная функция для получения количества выплат в год
  function getPaymentsPerYear(frequency) {
    switch (frequency) {
      case 'monthly': return 12;
      case 'quarterly': return 4;
      case 'semi_annually': return 2;
      case 'annually': return 1;
      default: return 4; // По умолчанию ежеквартально
    }
  }
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <DollarSign className="w-5 h-5 mr-2 text-green-500" />
        Быстрая настройка параметров
      </h3>
      
      <div className="space-y-5">
        {/* Сумма инвестиций */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="investmentSlider" className="text-gray-600 dark:text-gray-400">Сумма инвестиций</label>
            <span className="font-medium text-gray-900 dark:text-white">{currencySymbol} {formatNumber(initialInvestment, 0)}</span>
          </div>
          <input
            type="range"
            id="investmentSlider"
            min="1000"
            max="2000000"
            step="1000"
            value={initialInvestment}
            onChange={(e) => handleSetInvestment(parseFloat(e.target.value))}
            aria-label="Сумма инвестиций"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600"
          />
          
          {/* Кнопки с предопределенными суммами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedInvestments.map(amount => (
              <button
                key={amount.value}
                type="button"
                onClick={() => handleSetInvestment(parseFloat(amount.value))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${Math.abs(initialInvestment - parseFloat(amount.value)) < 1000
                    ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Дивидендная доходность */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="yieldSlider" className="text-gray-600 dark:text-gray-400">Дивидендная доходность</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatNumber(dividendYield, 2)}%</span>
          </div>
          <input
            type="range"
            id="yieldSlider"
            min="0.1"
            max="15"
            step="0.1"
            value={dividendYield}
            onChange={(e) => handleSetYield(parseFloat(e.target.value))}
            aria-label="Дивидендная доходность"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600"
          />
          
          {/* Кнопки с предопределенными доходностями */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedYields.map(yieldItem => (
              <button
                key={yieldItem.value}
                type="button"
                onClick={() => handleSetYield(parseFloat(yieldItem.value))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${Math.abs(dividendYield - parseFloat(yieldItem.value)) < 0.1
                    ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {yieldItem.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Период прогнозирования */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="periodSlider" className="text-gray-600 dark:text-gray-400">Период прогнозирования</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.projectionPeriod} {pluralize(parseFloat(formData.projectionPeriod) || 0, 'год', 'года', 'лет')}
            </span>
          </div>
          <input
            type="range"
            id="periodSlider"
            min="1"
            max="30"
            step="1"
            value={formData.projectionPeriod}
            onChange={(e) => handleSetPeriod(e.target.value)}
            aria-label="Период прогнозирования (лет)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600"
          />
          
          {/* Кнопки с предопределенными периодами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedPeriods.map(period => (
              <button
                key={period.value}
                type="button"
                onClick={() => handleSetPeriod(period.value)}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.projectionPeriod === period.value 
                    ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор периодичности выплат */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Периодичность выплат</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, dividendFrequency: 'quarterly' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.dividendFrequency === 'quarterly' 
                  ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Ежеквартально</div>
              <div className="text-xs opacity-75">4 раза в год</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, dividendFrequency: 'annually' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.dividendFrequency === 'annually' 
                  ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Ежегодно</div>
              <div className="text-xs opacity-75">1 раз в год</div>
            </button>
          </div>
        </div>
        
        {/* Включение роста дивидендов */}
        <div className="flex items-center">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={formData.includeDividendGrowth}
              onChange={() => setFormData(prev => ({ ...prev, includeDividendGrowth: !prev.includeDividendGrowth }))}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Учесть рост дивидендов
            </span>
          </label>
        </div>
        
        {/* Включение реинвестирования */}
        <div className="flex items-center">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={formData.includeReinvestment}
              onChange={() => setFormData(prev => ({ ...prev, includeReinvestment: !prev.includeReinvestment }))}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Реинвестировать дивиденды
            </span>
          </label>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateDividends}
          icon={<TrendingUp className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать дивиденды
        </Button>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, two, five) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return five;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return two;
  }
  return five;
};

export default QuickAdjustmentSidebar;